import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Check, MessageCircle, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { useSeo } from "@/pages/blog/useSeo";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { WHATSAPP_URL } from "@/components/landing/data";
import { GBT_STEPS, GBT_BENEFITS, GBT_CHECKLIST, GBT_FINDER_URL } from "@/content/gbt";
import { GbtCertificate } from "./GbtCertificate";
import { longDate } from "@/components/landing/SlotPicker";

const JSONLD = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  name: "Igiene professionale GBT certificata — Guided Biofilm Therapy",
  about: { "@type": "MedicalProcedure", name: "Guided Biofilm Therapy (GBT)", procedureType: "Preventive" },
  url: "https://centrodiparodontologia.com/igiene-gbt",
};

const Step = ({ s, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (i % 4) * 0.08 }}
    className={`rounded-3xl p-6 flex flex-col ${i % 3 === 1 ? "bg-[#1B3B28] text-[#FCFBF7]" : "bg-[#FCFBF7] border border-[#1B3B28]/10"}`}
    data-testid={`gbt-step-${s.n}`}
  >
    <span className={`f-mono text-xs tracking-[0.2em] ${i % 3 === 1 ? "text-[#86C89B]" : "text-[#3E6B48]"}`}>{s.n}</span>
    <h3 className={`f-serif text-2xl leading-tight mt-8 ${i % 3 === 1 ? "text-[#FCFBF7]" : "text-[#1B3B28]"}`}>{s.title}</h3>
    <p className={`text-sm leading-relaxed mt-3 ${i % 3 === 1 ? "text-[#FCFBF7]/70" : "text-[#526356]"}`}>{s.text}</p>
  </motion.div>
);

const Thanks = ({ done }) => (
  <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
    className="lg:col-span-12 flex items-start gap-4 rounded-3xl bg-[#1B3B28] text-[#FCFBF7] p-6" data-testid="gbt-booking-thanks">
    <CheckCircle2 size={22} className="text-[#86C89B] shrink-0 mt-0.5" />
    <p className="text-sm sm:text-base leading-relaxed">
      <b className="f-serif text-2xl font-normal block">Grazie, {done.name}. Richiesta ricevuta.</b>
      {done.slot ? `Hai scelto ${longDate(done.slot.date)} alle ${done.slot.time}: lo staff conferma l'orario via email.` : "Ti richiameremo noi per fissare la tua seduta di igiene GBT."}{done.emailSent && ` Ti abbiamo inviato un'email di conferma a ${done.email}.`} Intanto, ecco come si svolge.
    </p>
  </motion.div>
);

export default function GbtPage() {
  const done = useLocation().state;
  useSeo({
    title: "Igiene GBT certificata a Galliera | Guided Biofilm Therapy originale",
    description: "Igiene orale professionale con Guided Biofilm Therapy originale: studio certificato GBT a Galliera (BO). AIRFLOW®, PERIOFLOW® e PIEZON® PS, delicata anche per chi ha paura. Verifica la certificazione sul GBT Finder.",
    path: "/igiene-gbt",
    jsonLd: JSONLD,
  });
  return (
    <BlogLayout>
      <div data-testid="gbt-page">
        <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-36 sm:pt-44 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          {done?.name && <Thanks done={done} />}
          <div className="lg:col-span-7">
            <p className="eyebrow text-[#3E6B48]">Igiene professionale · GBT</p>
            <h1 className="f-serif text-5xl sm:text-6xl lg:text-7xl text-[#1B3B28] leading-[0.98] mt-6" data-testid="gbt-title">
              <MaskLine delay={0.1}>La pulizia dei denti,</MaskLine>
              <MaskLine delay={0.22}><em className="text-[#3E6B48]">come dovrebbe essere.</em></MaskLine>
            </h1>
            <Reveal delay={0.35}>
              <p className="text-lg text-[#526356] leading-relaxed mt-8 max-w-xl">
                La Guided Biofilm Therapy (GBT) è un protocollo in 8 fasi che rimuove il biofilm batterico, la vera causa di carie,
                gengivite e parodontite, in modo delicato e quasi senza fastidio. Il nostro è uno <b className="text-[#1B3B28]">studio certificato GBT originale</b>.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5"><GbtCertificate /></div>
        </section>

        <section className="bg-[#E6ECE4] py-24 sm:py-32" data-testid="gbt-verify-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-5">
              <p className="eyebrow text-[#3E6B48]">Non tutti gli studi sono certificati</p>
              <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] leading-[1.02] mt-5">Avere l'AIRFLOW® <em className="text-[#3E6B48]">non significa fare GBT.</em></h2>
              <p className="text-[#526356] leading-relaxed mt-6">
                Molti studi usano uno o più strumenti, ma solo gli studi certificati applicano il protocollo GBT originale completo, con
                formazione dello staff e standard verificati. Per essere sicuro, cerca lo studio sul sito ufficiale.
              </p>
              <a href={GBT_FINDER_URL} target="_blank" rel="noopener noreferrer" data-testid="gbt-finder-link-2"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-6 py-3 text-sm hover:bg-[#3E6B48] transition-colors">
                Cerca sul GBT Finder <ArrowUpRight size={15} />
              </a>
            </Reveal>
            <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
              <p className="f-serif text-2xl text-[#1B3B28]">Come riconoscere una vera GBT</p>
              <ul className="mt-6 space-y-3" data-testid="gbt-checklist">
                {GBT_CHECKLIST.map((t) => (
                  <li key={t} className="flex gap-3 rounded-2xl bg-[#FCFBF7] px-5 py-4 text-[#1E2922]">
                    <span className="grid place-items-center w-6 h-6 shrink-0 rounded-full bg-[#86C89B] text-[#0F2317]"><Check size={14} /></span>{t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 lg:px-10 py-24 sm:py-32" data-testid="gbt-steps-section">
          <p className="eyebrow text-[#3E6B48]">Il protocollo</p>
          <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] leading-[1.02] mt-5 max-w-2xl">Otto fasi, <em className="text-[#3E6B48]">sempre nello stesso ordine.</em></h2>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{GBT_STEPS.map((s, i) => <Step key={s.n} s={s} i={i} />)}</div>
        </section>

        <section className="bg-[#0F2317] py-24 sm:py-32" data-testid="gbt-benefits-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <h2 className="f-serif text-4xl sm:text-5xl text-[#FCFBF7] leading-[1.02] max-w-2xl">Perché la <em className="text-[#86C89B]">amano anche i pazienti più ansiosi.</em></h2>
            <div className="mt-14 grid gap-5 md:grid-cols-2">
              {GBT_BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.08}>
                  <div className="rounded-3xl border border-[#FCFBF7]/10 p-8 h-full">
                    <p className="f-serif text-3xl text-[#FCFBF7]">{b.title}</p>
                    <p className="text-[#FCFBF7]/65 mt-3 leading-relaxed">{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-6 lg:px-10 py-24 text-center" data-testid="gbt-cta">
          <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] leading-[1.05]">Prenota la tua igiene GBT, <em className="text-[#3E6B48]">con calma.</em></h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a href="/?trattamento=gbt#prenota" className="inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-6 py-3.5 text-sm hover:bg-[#3E6B48]" data-testid="gbt-cta-booking">Prenota l'igiene GBT <ArrowUpRight size={15} /></a>
            <a href={`${WHATSAPP_URL}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#1B3B28]/20 text-[#1B3B28] px-6 py-3.5 text-sm hover:bg-[#E6ECE4]" data-testid="gbt-cta-whatsapp"><MessageCircle size={15} /> Scrivici su WhatsApp</a>
          </div>
          <p className="text-xs text-[#526356] mt-10">GBT, Guided Biofilm Therapy, AIRFLOW®, PERIOFLOW® e PIEZON® sono marchi di EMS Electro Medical Systems S.A.</p>
        </section>
      </div>
    </BlogLayout>
  );
}
